import VersionManagementService from "@arcgis/core/versionManagement/VersionManagementService";
import "@arcgis/map-components/components/arcgis-feature-table";
import * as React from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { Bounce, ToastContainer } from "react-toastify";
import { AppConfig } from "../../AppConfig";
import { LeftPanelTabs, RightPanelTabs } from "../../constants/Panels";
import {
    AppContext,
    FeatureTableContext,
    JobContext,
    LighthouseContext,
    OverettContext,
    PendingLighthousePlacement,
    VersionContext,
} from "../../Context";
import { StoreState } from "../../store/rootReducer";
import { setBoatStatusOpen } from "../../store/appSlice";
import { setIsSectorEditTableOpen } from "../../store/lighthouseSlice";
import { getVersionedLayerById, zoomInClose } from "../../utils/arcgisUtils";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import { FeatureLayerNFS } from "../../extensions/FeatureLayerNFS";
import { LighthouseData } from "../../utils/lighthouseConstants";
import SidePanel from "../genericComponents/SidePanel/SidePanel";
import Header from "../Header/Header";
import MapControl from "../MapComponent/MapControl";
import CreateObjectControl from "../MapTools/CreateObject/CreateObjectControl";
import LayerlistControl from "../MapTools/Layerlist/LayerlistControl";
import SectorDefinitionTable from "../MapTools/LightHouse/SectorDefinitionTable";
import SectorSourceDialog from "../MapTools/LightHouse/SectorSourceDialog";
import SectorTable from "../MapTools/LightHouse/SectorTable";
import MeasurementControl from "../MapTools/Measurement/MeasurementControl";
import OverettChart from "../MapTools/Overett/OverettChart";
import LeftPanelContent from "../PanelContents/LeftPanelContents/LeftPanelContent";
import RightPanelContent from "../PanelContents/RightPanelContent/RightPanelContent";
import "./AuthenticatedContent.css";
import EditorViewModel from "@arcgis/core/widgets/Editor/EditorViewModel";
import BoatLocation from "../MapTools/BoatLocation/BoatLocation";
import NauticalImageWindow from "../FeatureViewer/NauticalImage/NauticalImageWindow";
import BoatStatus from "../Header/BoatStatus";
import type { ResourceHandle } from "@arcgis/core/core/Handles";
import type Graphic from "@arcgis/core/Graphic";
import type Point from "@arcgis/core/geometry/Point";
import type Layer from "@arcgis/core/layers/Layer";
import type Basemap from "@arcgis/core/Basemap";
import type WebMap from "@arcgis/core/WebMap";
import type BasemapGalleryViewModel from "@arcgis/core/widgets/BasemapGallery/BasemapGalleryViewModel";
import type { SearchResults } from "@arcgis/core/widgets/Search/types";

/** The same object, whatever graphic it arrived in: the layer and objectid are what identify it. */
const isSameFeature = (a: Graphic | null, b: Graphic | null) =>
    a === b ||
    (a !== null &&
        b !== null &&
        (a.layer as FeatureLayer | undefined)?.layerId === (b.layer as FeatureLayer | undefined)?.layerId &&
        a.attributes?.objectid === b.attributes?.objectid);

export function AuthenticatedContent() {
    const dispatch = useDispatch();
    const boatStatusDialogRef = React.useRef<HTMLDialogElement>(null);
    const [searchResults, setSearchResults] = React.useState<SearchResults[]>([]);
    const [clickResults, setClickResults] = React.useState<Graphic[]>([]);
    const [configWebMap, setConfigWebMap] = React.useState<WebMap | null | undefined>();
    const [basemapGalleryVM, setBasemapGalleryVM] = React.useState<BasemapGalleryViewModel | null>(null);
    const [themeMaps, setThemeMaps] = React.useState<WebMap[]>([]);
    const [portalBasemaps, setPortalBasemaps] = React.useState<Basemap[]>([]);

    const [activeTableLayer, setActiveTableLayer] = React.useState<Layer | undefined>(undefined);
    const [featureTableOpen, setFeatureTableOpen] = React.useState<boolean>(false);
    const featureTableRef = React.useRef<HTMLArcgisFeatureTableElement>(null);

    const [selectedFeature, setSelectedFeature] = React.useState<Graphic | null>(null);
    const [selectedFeatureHighlightRef, setSelectedFeatureHighlightRef] = React.useState<
        React.RefObject<ResourceHandle | null>
    >(React.useRef(null));
    const [featureEditsInFlight, setFeatureEditsInFlight] = React.useState<number>(0);
    const store = useStore<StoreState>();
    // Read by the selection guard below, which is created once and so cannot read the state itself
    const selectedFeatureRef = React.useRef<Graphic | null>(null);
    React.useEffect(() => {
        selectedFeatureRef.current = selectedFeature;
    }, [selectedFeature]);

    /**
     * Guarded version of setSelectedFeature: refuses to store a graphic whose `.layer`
     * is not the versioned variant of its layerId. Without this guard, certain code
     * paths (popup "open" action, click-results edit, related-object selection,
     * findAndSelectFeature trust chain) can leak a DEFAULT-bound layer reference into
     * selectedFeature, causing every subsequent edit to write directly into DEFAULT —
     * bypassing the version/QA workflow. See NFS2-1517 investigation.
     *
     * Behavior on detection:
     *   1. console.error with the layerId, incoming layer URL, and the graphic.
     *   2. console.trace so the offending call site is visible in DevTools.
     *   3. (DEV only) debugger; statement — DevTools will pause execution here, so you
     *      can step up the call stack and find which code attempted the leak.
     *   4. Look up the versioned counterpart by layerId and rebind incoming.layer to it.
     *   5. If no versioned counterpart exists, REFUSE to set (leaves selectedFeature
     *      unchanged) — better to break the flow than to silently route edits to DEFAULT.
     *
     * While the sector table edits a lighthouse, its save writes to whatever is selected.
     * Selecting another feature - from the map, the search, a related object - would point
     * that save at the wrong one, so the user is asked first and the table closes if they go
     * ahead. The table's own save reselects a clone of the same feature, which passes.
     */
    const setSelectedFeatureGuarded = React.useCallback(
        (incoming: Graphic | null) => {
            const isLeavingSectorEditTable =
                store.getState().lighthouse.isSectorEditTableOpen &&
                !isSameFeature(incoming, selectedFeatureRef.current);
            if (isLeavingSectorEditTable) {
                if (
                    !globalThis.confirm(
                        "Sektortabellen er åpen. Vil du lukke den og bytte objekt? Endringer i tabellen som ikke er lagret går tapt."
                    )
                ) {
                    return;
                }
                store.dispatch(setIsSectorEditTableOpen(false));
            }
            if (!incoming) {
                setSelectedFeature(null);
                return;
            }
            const incomingLayer = incoming.layer as FeatureLayerNFS | undefined;
            if (incomingLayer && incomingLayer.isVersioned === true) {
                setSelectedFeature(incoming);
                return;
            }
            const layerId = (incoming.layer as FeatureLayer | undefined)?.layerId;
            const incomingUrl = (incoming.layer as FeatureLayer | undefined)?.url;
            const versionedReplacement = getVersionedLayerById(layerId);
            console.error(
                "[selectedFeature guard] Attempt to set selectedFeature with a non-versioned layer caught. " +
                    `layerId=${layerId ?? "?"}, incomingUrl=${incomingUrl ?? "none"}, ` +
                    `rebound=${versionedReplacement ? "yes" : "no — refusing the set"}`,
                { incoming, incomingLayer: incoming.layer, versionedReplacement }
            );
            console.trace("[selectedFeature guard] call site");
            if (import.meta.env.DEV) {
                // Dev-only breakpoint. Open DevTools before reproducing the bug and execution
                // pauses here so you can walk up the stack to find the culprit.
                // eslint-disable-next-line no-debugger
                debugger;
            }
            if (!versionedReplacement) {
                // Refuse the set — don't leak a DEFAULT-bound feature.
                return;
            }
            incoming.layer = versionedReplacement;
            setSelectedFeature(incoming);
        },
        [store]
    );
    const [shownFeature, setShownFeature] = React.useState<Graphic | null>(null);
    const [efsSelectedFeatures, setEfsSelectedFeatures] = React.useState<Graphic[]>([]);

    const [morildExportSelectedFeatures, setMorildExportSelectedFeatures] = React.useState<Graphic[]>([]);
    const [seacableStartFeature, setSeacableStartFeature] = React.useState<Graphic | null>(null);
    const [seacableEndFeature, setSeacableEndFeature] = React.useState<Graphic | null>(null);

    const [lighthouseDataState, setLighthouseDataState] = React.useState<LighthouseData | null>(null);
    const [lighthouseData, setLighthouseData] = React.useState<React.RefObject<LighthouseData | null>>(
        React.useRef(null)
    );
    const [lighthouseSectorPanelsOpen, setLighthouseSectorPanelsOpen] = React.useState<Set<number>>(new Set());
    const [selectedLineRef, setSelectedLineRef] = React.useState<React.RefObject<Graphic | null>>(React.useRef(null));
    const [versionServiceRef, setVersionServiceRef] = React.useState<React.RefObject<VersionManagementService>>(
        React.useRef(
            new VersionManagementService({
                url: AppConfig.ArcGIS.BaseUrl + AppConfig.ArcGIS.VersionManagementService,
            })
        )
    );
    const [createObjectCurrentEntryRef, setCreateObjectCurrentEntryRef] = React.useState<
        React.RefObject<string | null>
    >(React.useRef(null));
    const [pendingLighthousePlacement, setPendingLighthousePlacement] = React.useState<
        React.RefObject<PendingLighthousePlacement | null>
    >(React.useRef(null));

    const [selectedJob, setSelectedJob] = React.useState<Graphic | null>(null);
    const [preLoadJobConfig, setPreLoadJobConfig] = React.useState<{ layerID: number; objectId: string } | null>(null);

    const [overettObjects, setOverettObjects] = React.useState<{
        upper: Graphic | null | undefined;
        lower: Graphic | null | undefined;
    }>({ upper: null, lower: null });
    const [overettLinjeRef, setOverettLinjeRef] = React.useState<React.RefObject<Graphic | null>>(React.useRef(null));
    const [overettHighlightsRef, setOverettHighlightsRef] = React.useState<React.RefObject<ResourceHandle[]>>(
        React.useRef<ResourceHandle[]>([])
    );
    const [overettObserver, setOverettObserver] = React.useState<Point | null>(null);
    const [editorVMUpperOverett, setEditorVMUpperOverett] = React.useState<
        React.RefObject<EditorViewModel | null | undefined>
    >(React.useRef(null));
    const [editorVMLowerOverett, setEditorVMLowerOverett] = React.useState<
        React.RefObject<EditorViewModel | null | undefined>
    >(React.useRef(null));

    const measurementToolOpen = useSelector((state: StoreState) => state.app.measureToolOpen);
    const createObjectOpen = useSelector((state: StoreState) => state.app.createObjectOpen);
    const isSectorTableOpen = useSelector((state: StoreState) => state.lighthouse.isSectorTableOpen);
    const isSectorEditTableOpen = useSelector((state: StoreState) => state.lighthouse.isSectorEditTableOpen);
    const sectorSourceStep = useSelector((state: StoreState) => state.lighthouse.sectorSourceStep);
    const isOverettChartOpen = useSelector((state: StoreState) => state.overett.isOverettChartOpen);
    const boatLocationWindowOpen = useSelector((state: StoreState) => state.app.boatLocationWindowOpen);
    const boatStatusOpen = useSelector((state: StoreState) => state.app.boatStatusOpen);
    const nauticalImageContext = useSelector((state: StoreState) => state.app.nauticalImageContext);

    const leftPanelActiveTab = useSelector((state: StoreState) => state.app.leftPanelActiveTab);
    const rightPanelActiveTab = useSelector((state: StoreState) => state.app.rightPanelActiveTab);
    const viewLoaded = useSelector((state: StoreState) => state.app.viewLoaded);

    const versionContextStore = {
        versionServiceRef: {
            set: setVersionServiceRef,
            value: versionServiceRef,
        },
    };

    const appContextStore = {
        selectedFeature: {
            set: setSelectedFeatureGuarded,
            value: selectedFeature,
        },
        shownFeature: {
            set: setShownFeature,
            value: shownFeature,
        },
        searchResults: {
            set: setSearchResults,
            value: searchResults,
        },
        clickResults: {
            set: setClickResults,
            value: clickResults,
        },
        efsSelectedFeatures: {
            add: (value: Graphic) => {
                setEfsSelectedFeatures((prevState) => [...prevState, value]);
            },
            remove: (value: Graphic) => {
                setEfsSelectedFeatures((prevState) =>
                    prevState.filter((obj) => obj.attributes.objectid !== value.attributes.objectid)
                );
            },
            value: efsSelectedFeatures,
            clear: () => {
                setEfsSelectedFeatures([]);
            },
        },
        morildExportSelectedFeatures: {
            add: (value: Graphic) => {
                setMorildExportSelectedFeatures((prevState) => [...prevState, value]);
            },
            remove: (value: Graphic) => {
                setMorildExportSelectedFeatures((prevState) =>
                    prevState.filter((obj) => obj.attributes.objectid !== value.attributes.objectid)
                );
            },
            addMany: (values: Graphic[]) => {
                const newFeatures = values.filter(
                    (v) => !morildExportSelectedFeatures.find((f) => f.attributes.objectid === v.attributes.objectid)
                );
                setMorildExportSelectedFeatures((prevState) => [...prevState, ...newFeatures]);
            },
            clear: () => {
                setMorildExportSelectedFeatures([]);
            },
            value: morildExportSelectedFeatures,
        },
        seacableStartFeature: {
            set: setSeacableStartFeature,
            value: seacableStartFeature,
        },
        seacableEndFeature: {
            set: setSeacableEndFeature,
            value: seacableEndFeature,
        },
        createObjectCurrentEntryRef: {
            set: setCreateObjectCurrentEntryRef,
            value: createObjectCurrentEntryRef,
        },
        pendingLighthousePlacement: {
            set: setPendingLighthousePlacement,
            value: pendingLighthousePlacement,
        },
        selectedFeatureHighlightRef: {
            set: setSelectedFeatureHighlightRef,
            value: selectedFeatureHighlightRef,
        },
        featureEditsInFlight: {
            value: featureEditsInFlight,
            // Clamp to ≥0 in case end() is called more times than begin() (defensive — should
            // not happen if every begin() is paired in a try/finally).
            begin: () => setFeatureEditsInFlight((n) => n + 1),
            end: () => setFeatureEditsInFlight((n) => Math.max(0, n - 1)),
        },
    };

    const lighthouseContextStore = {
        lighthouseData: {
            set: setLighthouseDataState,
            value: lighthouseDataState as LighthouseData,
        },
        lighthouseDataRef: {
            set: setLighthouseData,
            value: lighthouseData as React.RefObject<LighthouseData>,
        },
        lighthouseSectorPanelsOpen: {
            set: setLighthouseSectorPanelsOpen,
            value: lighthouseSectorPanelsOpen,
        },
        selectedLineRef: {
            set: setSelectedLineRef,
            value: selectedLineRef,
        },
    };

    const jobContextStore = {
        selectedJob: {
            set: setSelectedJob,
            value: selectedJob,
        },
        preLoadJobConfig: {
            set: setPreLoadJobConfig,
            value: preLoadJobConfig,
        },
    };

    const overettContextStore = {
        overettObjects: {
            set: setOverettObjects,
            value: overettObjects,
        },
        overettLinjeRef: {
            set: setOverettLinjeRef,
            value: overettLinjeRef,
        },
        overettHighlightsRef: {
            set: setOverettHighlightsRef,
            value: overettHighlightsRef,
            store: (handles: ResourceHandle[] | null, clear: boolean = true) => {
                if (!overettHighlightsRef || !overettHighlightsRef.current) return;
                if (clear) {
                    for (const handle of overettHighlightsRef.current) {
                        if (handle && handle.remove) {
                            handle.remove();
                        }
                    }
                    overettHighlightsRef.current = [];
                }
                if (handles && handles.length > 0) {
                    overettHighlightsRef.current.push(...handles);
                }
            },
            clear: () => {
                if (!overettHighlightsRef || !overettHighlightsRef.current) return;
                for (const handle of overettHighlightsRef.current) {
                    if (handle && handle.remove) {
                        handle.remove();
                    }
                }
                overettHighlightsRef.current = [];
            },
        },
        overettObserver: {
            set: setOverettObserver,
            value: overettObserver,
        },
        editorVMUpperOverett: {
            set: setEditorVMUpperOverett,
            value: editorVMUpperOverett,
        },
        editorVMLowerOverett: {
            set: setEditorVMLowerOverett,
            value: editorVMLowerOverett,
        },
    };

    const featureTableContextStore = {
        activeLayer: {
            set: setActiveTableLayer,
            value: activeTableLayer,
        },
        isOpen: {
            set: setFeatureTableOpen,
            value: featureTableOpen,
        },
    };

    const getRightPanelClass = () => {
        switch (rightPanelActiveTab) {
            case RightPanelTabs.EFS_MANAGER:
                return "efs";
            case RightPanelTabs.SECTOR:
                return "sector-editor";
        }

        return "";
    };

    React.useEffect(() => {
        if (featureTableRef.current) {
            // @ts-ignore attribute is actually nullable
            featureTableRef.current.layer = activeTableLayer;
        }
    }, [activeTableLayer]);

    React.useEffect(() => {
        if (boatStatusOpen && boatStatusDialogRef.current) {
            boatStatusDialogRef.current.showModal();
        } else if (!boatStatusOpen && boatStatusDialogRef.current) {
            boatStatusDialogRef.current.close();
        }
    }, [boatStatusOpen]);

    return (
        <VersionContext.Provider value={versionContextStore}>
            <AppContext.Provider value={appContextStore}>
                <LighthouseContext.Provider value={lighthouseContextStore}>
                    <JobContext.Provider value={jobContextStore}>
                        <OverettContext.Provider value={overettContextStore}>
                            <FeatureTableContext.Provider value={featureTableContextStore}>
                                <Header />
                                <div className="map-container">
                                    <SidePanel
                                        side="left"
                                        collapseButtonHidden={leftPanelActiveTab === LeftPanelTabs.UNDEFINED}
                                    >
                                        <LeftPanelContent />
                                    </SidePanel>
                                    <div className="flex-col h-100 w-100 map-background">
                                        <MapControl />
                                        {viewLoaded && (
                                            <div
                                                style={{
                                                    position: featureTableOpen ? "relative" : "absolute",
                                                    bottom: featureTableOpen ? "0" : "-40vh",
                                                    // display: `${featureTableOpen ? "block" : "none"}`,
                                                    height: "40vh",
                                                }}
                                            >
                                                <arcgis-feature-table
                                                    id="job-details-feature-table"
                                                    ref={featureTableRef}
                                                    style={{ width: "100%", height: "100%" }}
                                                    referenceElement="map"
                                                    showLayerDropdown={true}
                                                    relatedRecordsEnabled
                                                    noDataMessage={"No features found."}
                                                    returnGeometryEnabled={true}
                                                    actionColumnConfig={{
                                                        label: "Zoom to feature",
                                                        icon: "zoom-to-object",
                                                        callback: ({ feature }) => {
                                                            zoomInClose(feature);
                                                        },
                                                    }}
                                                ></arcgis-feature-table>
                                            </div>
                                        )}
                                    </div>

                                    <SidePanel
                                        collapseButtonHidden={rightPanelActiveTab === RightPanelTabs.UNDEFINED}
                                        panelClass={getRightPanelClass()}
                                        side="right"
                                    >
                                        <RightPanelContent />
                                    </SidePanel>

                                    {measurementToolOpen && <MeasurementControl />}
                                    {createObjectOpen && <CreateObjectControl />}
                                    {isSectorTableOpen && <SectorTable />}
                                    {sectorSourceStep === "choosing" && <SectorSourceDialog />}
                                    {sectorSourceStep === "table" && <SectorDefinitionTable mode="create" />}
                                    {isSectorEditTableOpen && <SectorDefinitionTable mode="edit" />}
                                    <BoatLocation boatLocationWindowOpen={boatLocationWindowOpen} />
                                    <LayerlistControl
                                        configWebMap={configWebMap}
                                        setConfigWebMap={setConfigWebMap}
                                        basemapGalleryVM={basemapGalleryVM}
                                        setBasemapGalleryVM={setBasemapGalleryVM}
                                        themeMaps={themeMaps}
                                        setThemeMaps={setThemeMaps}
                                        portalBasemaps={portalBasemaps}
                                        setPortalBasemaps={setPortalBasemaps}
                                    />
                                    {nauticalImageContext.open && <NauticalImageWindow />}

                                    {isOverettChartOpen && <OverettChart />}
                                </div>
                                {boatStatusOpen && (
                                    <dialog
                                        ref={boatStatusDialogRef}
                                        style={{
                                            border: "none",
                                            borderRadius: "8px",
                                            padding: "2rem",
                                            width: "90%",
                                            height: "90%",
                                            overflow: "auto",
                                            backgroundColor: "var(--surface-surface-primary, #fff)",
                                        }}
                                        onClick={(e) => {
                                            if (e.target === boatStatusDialogRef.current) {
                                                dispatch(setBoatStatusOpen(false));
                                            }
                                        }}
                                    >
                                        <BoatStatus onClose={() => dispatch(setBoatStatusOpen(false))} />
                                    </dialog>
                                )}
                                <ToastContainer
                                    position="top-center"
                                    autoClose={5000}
                                    hideProgressBar={false}
                                    newestOnTop={false}
                                    closeOnClick
                                    rtl={false}
                                    pauseOnFocusLoss
                                    draggable
                                    pauseOnHover
                                    theme="light"
                                    transition={Bounce}
                                />
                            </FeatureTableContext.Provider>
                        </OverettContext.Provider>
                    </JobContext.Provider>
                </LighthouseContext.Provider>
            </AppContext.Provider>
        </VersionContext.Provider>
    );
}
